import { useMemo, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { GoalFilters } from "../../pages/GoalsPage";
import { GoalDuration } from "../../types/goals";
import DateRangePicker from "./DateRangePicker";
import Select from "./Select";

export const FilterSection: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => (
    <div className="flex flex-wrap items-center gap-3 w-full">
        {children}
    </div>
);

// Custom hook for debounced value
function useDebounce<T>(value: T, delay: number): T {
    // State to store the debounced value
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        // Set a timeout to update the debounced value after the specified delay
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        // Clean up the timeout if the value changes or the component unmounts
        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);

    return debouncedValue;
}

const GoalsFilters: React.FC<{
    filters: GoalFilters;
    onChange: (filters: GoalFilters) => void;
}> = ({ filters, onChange }) => {
    const { t } = useTranslation();

    const [searchTerm, setSearchTerm] = useState(filters.searchTerm || '');

    const debouncedSearchTerm = useDebounce(searchTerm, 1000);

    useEffect(() => {
        if (debouncedSearchTerm !== filters.searchTerm) {
            onChange({ ...filters, searchTerm: debouncedSearchTerm, page: 1 });
        }
    }, [debouncedSearchTerm, filters, onChange]);

    const durationOptions = useMemo(() => ([
        { value: '', label: t('goals.duration.all') },
        { value: 'short', label: t('goals.duration.short') },
        { value: 'medium', label: t('goals.duration.medium') },
        { value: 'long', label: t('goals.duration.long') }
    ]), [t]);

    const statusOptions = useMemo(() => ([
        { value: '', label: t('goals.status.all') },
        { value: 'true', label: t('goals.status.completed') },
        { value: 'false', label: t('goals.status.inProgress') }
    ]), [t]);

    const sortOptions = useMemo(() => ([
        { value: 'createdAt-desc', label: t('goals.sort.newest') },
        { value: 'createdAt-asc', label: t('goals.sort.oldest') },
        { value: 'progress-desc', label: t('goals.sort.mostProgress') },
        { value: 'progress-asc', label: t('goals.sort.leastProgress') }
    ]), [t]);

    const handleDurationChange = (value: string) => {
        onChange({
            ...filters,
            duration: value === '' ? undefined : value as GoalDuration,
            page: 1
        });
    };

    const handleStatusChange = (value: string) => {
        const completed = value === ''
            ? undefined
            : value === 'true';

        onChange({ ...filters, completed, page: 1 });
    };

    return (
        <div className="bg-white dark:bg-gray-900 p-5 rounded-lg shadow-sm border dark:border-gray-700">
            <div className="space-y-4">
                {/* Primera fila: Búsqueda y filtros principales */}
                <FilterSection>
                    <div className="w-full md:w-1/3 lg:w-1/4">
                        <input
                            type="text"
                            placeholder={t('goals.search')}
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                            }}
                            className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-lg w-full border dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 outline-none"
                        />
                    </div>

                    <div className="w-full md:w-auto">
                        <Select
                            value={filters.duration ?? ''}
                            onChange={handleDurationChange}
                            options={durationOptions}
                            className="w-full md:w-auto dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
                        />
                    </div>

                    <div className="w-full md:w-auto">
                        <Select
                            value={filters.completed === undefined ? '' : String(filters.completed)}
                            onChange={handleStatusChange}
                            options={statusOptions}
                            className="w-full md:w-auto dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
                        />
                    </div>

                    <div className="w-full md:w-auto">
                        <Select
                            value={`${filters.sortBy}-${filters.sortOrder}`}
                            onChange={(value) => {
                                const [sortBy, sortOrder] = value.split('-');
                                onChange({ ...filters, sortBy, sortOrder: sortOrder as 'asc' | 'desc' });
                            }}
                            options={sortOptions}
                            className="w-full md:w-auto dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
                        />
                    </div>
                </FilterSection>

                {/* Segunda fila: Selector de fechas */}
                {/* <FilterSection>
                    <div className="w-full">
                        <DateRangePicker
                            startDate={filters.startDate}
                            endDate={filters.endDate}
                            onChange={({ startDate, endDate }) =>
                                onChange({ ...filters, startDate, endDate, page: 1 })
                            }
                        />
                    </div>
                </FilterSection> */}
            </div>
        </div>
    );
};

export default GoalsFilters;